// The campaign-table sort/filter/facet helpers, ported verbatim from
// Meteoric's `src/lib/profit-yield-view.ts` (the campaign-table section) plus
// `marketplaceCodeLabel` from its `src/lib/naming.ts` (W6, parity restore).
// Pure functions only - no query, no fetch, no React.

import type { Tone } from "@/lib/tone";

import type { CampaignRow, Verdict } from "./api";

export const UNMAPPED_PRODUCT_LABEL = "Brand / multiple";

export type SortKey =
  | "product"
  | "name"
  | "ad_product"
  | "marketplace"
  | "spend_gbp"
  | "attr_sales_gbp"
  | "attr_units"
  | "profit_gbp"
  | "profit_per_pound"
  | "coverage_pct"
  | "verdict";

export type SortDir = "asc" | "desc";

export type CampaignFilters = {
  verdicts: Set<Verdict>;
  product: string | null;
  variant: string | null;
  marketplace: string | null;
  type: string | null;
  query: string;
  losingOnly: boolean;
};

export type FacetOptions = {
  products: string[];
  variantsByProduct: Record<string, string[]>;
  marketplaces: string[];
  types: string[];
};

// Severity rank: cut is the most actionable, so it leads on ascending sort.
export const VERDICT_SEVERITY: Record<Verdict, number> = {
  cut: 0,
  hold: 1,
  scale: 2,
  "insufficient-data": 3,
};

export const NUMERIC_SORT_KEYS = new Set<SortKey>([
  "spend_gbp",
  "attr_sales_gbp",
  "attr_units",
  "profit_gbp",
  "profit_per_pound",
  "coverage_pct",
]);

export function emptyFilters(): CampaignFilters {
  return {
    verdicts: new Set(),
    product: null,
    variant: null,
    marketplace: null,
    type: null,
    query: "",
    losingOnly: false,
  };
}

export function hasActiveFilters(filters: CampaignFilters): boolean {
  return (
    filters.verdicts.size > 0 ||
    filters.product !== null ||
    filters.variant !== null ||
    filters.marketplace !== null ||
    filters.type !== null ||
    filters.query.trim() !== "" ||
    filters.losingOnly
  );
}

export function sortCampaignRows<T extends CampaignRow>(rows: T[], key: SortKey, dir: SortDir): T[] {
  const factor = dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const aNull = isNullForKey(a, key);
    const bNull = isNullForKey(b, key);
    if (aNull && bNull) {
      return 0;
    }
    // Nulls sink to the bottom regardless of sort direction.
    if (aNull) {
      return 1;
    }
    if (bNull) {
      return -1;
    }
    return compareRows(a, b, key) * factor;
  });
}

export function filterCampaignRows<T extends CampaignRow>(rows: T[], filters: CampaignFilters): T[] {
  const query = filters.query.trim().toLowerCase();
  return rows.filter((row) => {
    if (filters.verdicts.size > 0 && !filters.verdicts.has(row.verdict)) {
      return false;
    }
    if (filters.product !== null) {
      if (filters.product === UNMAPPED_PRODUCT_LABEL) {
        if (row.product !== null) {
          return false;
        }
      } else if (row.product !== filters.product) {
        return false;
      }
    }
    if (filters.variant !== null && row.variant !== filters.variant) {
      return false;
    }
    if (filters.marketplace !== null && row.marketplace !== filters.marketplace) {
      return false;
    }
    if (filters.type !== null && row.ad_product !== filters.type) {
      return false;
    }
    if (query !== "" && !row.name.toLowerCase().includes(query)) {
      return false;
    }
    if (filters.losingOnly && !((row.profit_gbp ?? 0) < 0)) {
      return false;
    }
    return true;
  });
}

export function facetOptions(rows: CampaignRow[]): FacetOptions {
  const products = new Set<string>();
  const variantsByProduct: Record<string, Set<string>> = {};
  const marketplaces = new Set<string>();
  const types = new Set<string>();
  let hasUnmapped = false;

  for (const row of rows) {
    if (row.product === null) {
      hasUnmapped = true;
    } else {
      products.add(row.product);
      const variants = (variantsByProduct[row.product] ??= new Set());
      if (row.variant !== null) {
        variants.add(row.variant);
      }
    }
    if (row.marketplace) {
      marketplaces.add(row.marketplace);
    }
    if (row.ad_product) {
      types.add(row.ad_product);
    }
  }

  const productList = [...products].sort((a, b) => a.localeCompare(b));
  const byProduct: Record<string, string[]> = {};
  for (const product of productList) {
    byProduct[product] = [...(variantsByProduct[product] ?? new Set<string>())].sort((a, b) =>
      a.localeCompare(b),
    );
  }
  if (hasUnmapped) {
    productList.push(UNMAPPED_PRODUCT_LABEL);
    byProduct[UNMAPPED_PRODUCT_LABEL] = [];
  }

  return {
    products: productList,
    variantsByProduct: byProduct,
    marketplaces: [...marketplaces].sort((a, b) => a.localeCompare(b)),
    types: [...types].sort((a, b) => a.localeCompare(b)),
  };
}

export function isNullForKey(row: CampaignRow, key: SortKey): boolean {
  if (key === "verdict") {
    return false;
  }
  if (NUMERIC_SORT_KEYS.has(key)) {
    return (row[key] as number | null) === null;
  }
  if (key === "product") {
    return row.product === null;
  }
  return false;
}

export function compareRows(a: CampaignRow, b: CampaignRow, key: SortKey): number {
  if (key === "verdict") {
    return VERDICT_SEVERITY[a.verdict] - VERDICT_SEVERITY[b.verdict];
  }
  if (NUMERIC_SORT_KEYS.has(key)) {
    return (a[key] as number) - (b[key] as number);
  }
  if (key === "product") {
    const byProduct = (a.product as string).localeCompare(b.product as string);
    return byProduct !== 0 ? byProduct : (a.variant ?? "").localeCompare(b.variant ?? "");
  }
  return (a[key] as string).localeCompare(b[key] as string);
}

// --- marketplace short-code -> friendly label (ported from Meteoric naming.ts) ---

const MARKETPLACE_CODE_LABELS: Record<string, string> = {
  UK: "Amazon UK",
  US: "Amazon US",
  DE: "Amazon Germany",
  FR: "Amazon France",
  ES: "Amazon Spain",
  IT: "Amazon Italy",
  NL: "Amazon Netherlands",
  BE: "Amazon Belgium",
  PL: "Amazon Poland",
  IE: "Amazon Ireland",
  SE: "Amazon Sweden",
  AE: "Amazon UAE",
  SA: "Amazon Saudi Arabia",
};

/**
 * The campaign verdict's tone (W8, parity polish; Meteoric's
 * `verdictPillToken`). `scale` is worth acting on, `cut` is the loss, `hold`
 * is a plain steady-state (the neutral tertiary text, not a colour), and
 * anything else - today only `insufficient-data` - is a caution: there is
 * not yet enough to call it either way.
 */
export function verdictTone(verdict: Verdict): Tone {
  if (verdict === "scale") return "ok";
  if (verdict === "hold") return "neutral";
  if (verdict === "cut") return "critical";
  return "watch";
}

/**
 * Friendly label for a marketplace short code. A known code maps to "Amazon
 * <Country>"; an unknown-but-present value passes through UNCHANGED so a
 * non-code string is never mangled; an empty value renders "-".
 */
export function marketplaceCodeLabel(code: string | null | undefined): string {
  const trimmed = typeof code === "string" ? code.trim() : "";
  if (!trimmed) return "-";
  return MARKETPLACE_CODE_LABELS[trimmed.toUpperCase()] ?? trimmed;
}
